import { formatInTimeZone } from 'date-fns-tz';
import { memo, useEffect, useState } from 'react';

import RenderRichText from '@/components/RenderRichText';
import { Modal, Pill } from '@/ui';
import { Avatar } from '@/ui/Avatar/Avatar';

import type { CalendarEvent, CalendarPerson } from '../../types';

import type { EventDetailsModalProps } from './types';

function PersonRow({ person, sensitive = false }: { person: CalendarPerson; sensitive?: boolean }) {
  return (
    <li className={`${sensitive ? 'mp-sensitive ' : ''}flex items-center gap-2 py-0.5`}>
      <Avatar src={person.avatarUrl} name={person.name} size="xs" />
      <span>{person.name}</span>
    </li>
  );
}

function formatTimeRange(event: CalendarEvent, timeZone: string): string {
  const zone = event.timeZone ?? timeZone;
  if (event.isAllDay) {
    return formatInTimeZone(event.start, zone, 'EEEE, MMMM d, yyyy');
  }
  const sameDay =
    formatInTimeZone(event.start, zone, 'yyyy-MM-dd') ===
    formatInTimeZone(event.end, zone, 'yyyy-MM-dd');
  const startLabel = formatInTimeZone(event.start, zone, 'EEEE, MMMM d, yyyy · h:mm a');
  const endLabel = formatInTimeZone(
    event.end,
    zone,
    sameDay ? 'h:mm a' : 'EEEE, MMMM d, yyyy · h:mm a'
  );
  return `${startLabel} – ${endLabel}`;
}

function EventDetailsModal({ event, timeZone, onClose }: EventDetailsModalProps) {
  const [displayedEvent, setDisplayedEvent] = useState(event);

  useEffect(() => {
    if (event) setDisplayedEvent(event);
  }, [event]);

  return (
    <Modal open={Boolean(event)} onClose={onClose} title={displayedEvent?.title} size="md">
      {displayedEvent && (
        <div className="flex flex-col gap-4 text-sm">
          {(displayedEvent.isCancelled ||
            displayedEvent.isNoShow ||
            displayedEvent.isConfirmed) && (
            <div className="flex flex-wrap gap-1.5">
              {displayedEvent.isCancelled && (
                <Pill color="destructive" size="sm">
                  Cancelled
                </Pill>
              )}
              {displayedEvent.isNoShow && (
                <Pill color="warning" size="sm">
                  No-show
                </Pill>
              )}
              {displayedEvent.isConfirmed && (
                <Pill color="primary" size="sm">
                  Confirmed
                </Pill>
              )}
            </div>
          )}

          <div>
            <span className="block text-xs font-medium uppercase tracking-wide text-uniform-gray-500 dark:text-uniform-gray-400">
              When
            </span>
            <span className="text-uniform-gray-900 dark:text-uniform-gray-100">
              {formatTimeRange(displayedEvent, timeZone)}
            </span>
          </div>

          {displayedEvent.location && (
            <div>
              <span className="block text-xs font-medium uppercase tracking-wide text-uniform-gray-500 dark:text-uniform-gray-400">
                Location
              </span>
              <span className="text-uniform-gray-900 dark:text-uniform-gray-100">
                {displayedEvent.location}
              </span>
            </div>
          )}

          {displayedEvent.providers && displayedEvent.providers.length > 0 && (
            <div>
              <span className="block text-xs font-medium uppercase tracking-wide text-uniform-gray-500 dark:text-uniform-gray-400">
                Providers
              </span>
              <ul className="text-uniform-gray-900 dark:text-uniform-gray-100">
                {displayedEvent.providers.map((provider) => (
                  <PersonRow key={provider.id} person={provider} />
                ))}
              </ul>
            </div>
          )}

          {displayedEvent.participants && displayedEvent.participants.length > 0 && (
            <div>
              <span className="block text-xs font-medium uppercase tracking-wide text-uniform-gray-500 dark:text-uniform-gray-400">
                Participants
              </span>
              <ul className="text-uniform-gray-900 dark:text-uniform-gray-100">
                {displayedEvent.participants.map((participant) => (
                  <PersonRow key={participant.id} person={participant} sensitive />
                ))}
              </ul>
            </div>
          )}

          {displayedEvent.description && (
            <div>
              <span className="block text-xs font-medium uppercase tracking-wide text-uniform-gray-500 dark:text-uniform-gray-400">
                Description
              </span>
              <RenderRichText
                className="prose-sm text-uniform-gray-900 dark:prose-invert dark:text-uniform-gray-100"
                body={displayedEvent.description}
              />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

export default memo(EventDetailsModal);
